import { isNumber } from "lodash";
import { getLeafValue } from "hooks/widget-data/datapoints-api/helper";
const createTooltip = (mainLabel, category, seriesLabel, value) => {
    const labelValuePairs = [];
    if (category) {
        labelValuePairs.push([mainLabel, category]);
    }
    labelValuePairs.push([seriesLabel, value]);
    return labelValuePairs;
};
const createTooltipFromKeys = (tooltipKeys, category, measures, seriesLabel) => {
    const tooltipItems = [];
    tooltipKeys.forEach((key) => {
        switch (key.type) {
            case "header":
                if (category) {
                    tooltipItems.push({ label: seriesLabel, type: "header", value: category });
                }
                break;
            case "value":
                if (key.measureKey && measures?.[key.measureKey] !== undefined) {
                    const value = measures[key.measureKey];
                    tooltipItems.push([key.label ?? key.measureKey, value?.toString() ?? ""]);
                }
                break;
            case "separator":
                tooltipItems.push({ type: "separator" });
                break;
            case "icon":
                tooltipItems.push({ icon: key.icon ?? "", label: key.label, type: "icon" });
                break;
            default:
                break;
        }
    });
    return tooltipItems;
};
const createPoint = (data, category, values, series, tooltip, configMeasure, xaxis) => {
    if (series && values && category) {
        if (!data[series]) {
            data[series] = [];
        }
        data[series].push({
            custom: { id: configMeasure.name, tooltip },
            name: category || series,
            type: configMeasure.type,
            ...values,
            className: configMeasure.className,
            format: configMeasure.format,
            order: configMeasure.order,
            yAxis: configMeasure.yAxis
        });
        if (!xaxis.includes(category)) {
            xaxis.push(category);
        }
    }
};
const validateAndPrepare = (args, params, predefinedSeriesMap) => {
    if ((args.context.staticData?.customResult?.length ?? 0) > 0 || params.yaxis?.length <= 0 || args.context.staticData.root.length <= 0) {
        return false;
    }
    args.context.staticData.root[0].additionalData = { yaxis: params.yaxis };
    if (params.xaxisTitle) {
        args.context.staticData.root[0].additionalData.xaxisTitle = params.xaxisTitle;
    }
    args.context.staticData.customResult = [];
    params.predefinedSeries?.forEach((x) => {
        predefinedSeriesMap[x.name] = x;
    });
    return true;
};
const createSeries = (args, data, predefinedSeriesMap, hideSeries) => {
    Object.entries(data).forEach(([seriesName, series]) => {
        const predefinedSeries = predefinedSeriesMap[seriesName];
        const showSeries = hideSeries ? data[seriesName].some((val) => val.y !== 0) : true;
        if (showSeries) {
            args.context.staticData.customResult?.push({
                className: data[seriesName][0].className,
                colorIndex: predefinedSeries?.colorIndex,
                data: data[seriesName],
                events: { legendItemClick: () => true },
                name: predefinedSeries?.displayName ?? seriesName,
                order: series[0].order,
                threshold: 0,
                type: predefinedSeries?.seriesType ?? (series[0].type ?? "column"),
                valueFormat: series[0].format,
                yAxis: predefinedSeries?.yaxis ?? (series[0].yAxis ?? 0)
            });
        }
    });
};
const formatSubtitle = (subtitleFormat, subtitle) => {
    if (subtitleFormat && subtitle) {
        return subtitleFormat.replace("$0", subtitle);
    }
    return subtitle;
};
const isValid = (measure, significantZeros) => {
    if (significantZeros) {
        const value = getLeafValue(measure);
        return isNumber(value);
    }
    // Keep this bit for background-compatibility
    return measure?.amount || isNumber(measure);
};
const getFilter = (args, params) => {
    if (!args.context.componentProps?.parentId) {
        return null;
    }
    const filter = args.context.widgetState?.masterDetailsChoice;
    return (filter ?? "") === "" ? params.measures?.[0].name : filter;
};
const getTooltipValue = (measure, measures, predefinedSeries) => {
    if (measure.formatedLow && measure.formatedHigh) {
        return `${measures?.[measure.formatedLow]} - ${measures?.[measure.formatedHigh]}`;
    }
    const formatKey = predefinedSeries?.useSecondFormat ? measure.formated2 : measure.formated;
    return measures?.[formatKey ?? measure.name];
};
const buildTooltip = (params, measure, category, measures, seriesDisplayName) => {
    const tooltipKeys = measure.tooltipKeys ?? params.tooltipKeys;
    if (tooltipKeys) {
        return createTooltipFromKeys(tooltipKeys, category, measures, seriesDisplayName);
    }
    return createTooltip(params.tooltipLabel ?? "", category, seriesDisplayName, getTooltipValue(measure, measures));
};
const processMeasure = (params, measure, dataItem, predefinedSeriesMap, data, xaxis) => {
    const labelName = measure.category ? dataItem.measures?.[measure.category ?? ""] : measure.label;
    const seriesName = (params.isTransponed ? labelName : dataItem.dimension)?.toString() ?? "";
    const category = params.isTransponed ? dataItem.dimension : labelName;
    const predefinedSeries = predefinedSeriesMap[seriesName];
    const seriesDisplayName = predefinedSeries?.displayName ?? seriesName;
    const tooltip = buildTooltip(params, measure, category, dataItem.measures ?? {}, seriesDisplayName);
    const hasValidMeasure = dataItem.measures?.[measure.high] ||
        dataItem.measures?.[measure.low] ||
        isValid(dataItem.measures?.[measure.name], measure.showZeros);
    if (hasValidMeasure) {
        createPoint(data, category, {
            high: dataItem.measures?.[measure.high],
            low: dataItem.measures?.[measure.low],
            y: getLeafValue(dataItem.measures?.[measure.name])
        }, seriesName, tooltip, measure, xaxis);
    }
};
const processDataCollection = (params, mainCollection, filter, predefinedSeriesMap, data, xaxis) => {
    const filteredMeasures = params.measures?.filter((f) => !filter || f.name === filter) ?? [];
    mainCollection?.forEach((dataItem) => {
        filteredMeasures.forEach((measure) => {
            processMeasure(params, measure, dataItem, predefinedSeriesMap, data, xaxis);
        });
    });
};
const updateAdditionalData = (args, params, filter, xaxis) => {
    const hasTooltipKeys = Boolean(params.tooltipKeys || params.measures?.some((m) => m.tooltipKeys));
    const subtitleLabel = params.measures.filter((f) => f.name === filter)?.[0]?.label ??
        args.visitable.measures?.[params?.defaultSubTitleMeasureName ?? ""];
    args.context.staticData.root[0].additionalData = {
        ...args.context.staticData.root[0].additionalData,
        hasTooltipKeys,
        subtitle: formatSubtitle(args.context?.componentProps?.subtitleFormat, subtitleLabel),
        xaxis
    };
};
export const stackedColumnChart = (params) => (args) => {
    const predefinedSeriesMap = {};
    if (!validateAndPrepare(args, params, predefinedSeriesMap)) {
        return;
    }
    const filter = getFilter(args, params);
    const hideZeroSeries = params.measures?.[0].hideZeroValueSeries ?? false;
    const mainCollection = args.context.staticData.root[0]?.data ?? args.context.staticData.root;
    const data = {};
    const xaxis = [];
    processDataCollection(params, mainCollection, filter, predefinedSeriesMap, data, xaxis);
    createSeries(args, data, predefinedSeriesMap, hideZeroSeries);
    updateAdditionalData(args, params, filter, xaxis);
};
